import { Icon } from '@natera/material/lib/icon'
import { useQuery } from '@tanstack/react-query'
import { format, isToday, isYesterday } from 'date-fns'
import { uniqBy } from 'lodash-es'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router'
import { EOB_CLASSIFICATIONS, isNonFinancialEob, useSubcategories } from '~/entities/categories'
import {
  getIsClassificationFinished,
  isTaggingClassification,
  PRIOR_AUTH_PHASES,
  useAuditTrailEvents,
  useCurrentDocument,
} from '~/entities/document'
import { usePayors } from '~/entities/payors'
import { getDisplayName } from '~/entities/reviewer'
import { apiClient } from '~/shared/api/data-ingestion-api'
import { cn } from '~/shared/lib/cn'
import { useIsFeatureEnabled } from '~/shared/lib/feature-flags'
import {
  Button,
  Chip,
  CollapsibleContent,
  CollapsibleRoot,
  CollapsibleTrigger,
  IconButton,
  Imagery,
  InfoTip,
  LocalIcon,
  ScrollArea,
  Text,
} from '~/shared/ui'
import { EOB_NON_FINANCIAL_STEPS, EOB_STEPS, STEPS, STEPS_WITH_TAGGING } from '../config'
import { getEventComponent, getSearchableText, getStepLabel, processEvents } from '../lib'
import type { AuditTrailStep } from '../model'
import { useAuditTrail, useAutoSetAuditTrailStep } from '../model'
import { ReviewerFilter } from './reviewer-filter'
import { SearchFilter } from './search-filter'
import { EventShell } from './shared'
import { StepFilter } from './step-filter'

interface AuditTrailProps {
  className?: string
  page: AuditTrailStep
  assetId: string
}

export function AuditTrail({ className, page, assetId }: AuditTrailProps) {
  useAutoSetAuditTrailStep({ page })
  const {
    closeAuditTrail,
    step,
    handleSetStep,
    reviewerEmails,
    setReviewerEmails,
    clearFilters,
    isDirty,
    searchQuery,
    setSearchQuery,
  } = useAuditTrail({ page })
  const [sorting, setSorting] = useState<'newest' | 'oldest'>('newest')
  const documentKey = useParams<'documentKey'>().documentKey as string
  const {
    data: { document },
  } = useCurrentDocument()
  const {
    data: { events: serverEvents },
  } = useAuditTrailEvents({ documentKey })
  const subcategories = useSubcategories()
  const {
    data: {
      data: { insurancePayors: payors },
    },
  } = usePayors()
  const isTaggingEnabled = isTaggingClassification(document?.category, subcategories)
  const isHideLlmRarcCarcEnabled = useIsFeatureEnabled('2026-07-hide-llm-rarc-carc')
  const isEobEnabled = useIsFeatureEnabled('2026-02-eob-phase-1')
  const isEobClassification =
    isEobEnabled &&
    EOB_CLASSIFICATIONS.includes(document.category as (typeof EOB_CLASSIFICATIONS)[number])
  const isNonFinancial = isEobClassification && isNonFinancialEob(document.category)
  const stepFilterOptions = isNonFinancial
    ? EOB_NON_FINANCIAL_STEPS
    : isEobClassification
      ? EOB_STEPS
      : isTaggingEnabled
        ? STEPS_WITH_TAGGING
        : STEPS
  const hideLlmRarcCarcUntilReviewed = isHideLlmRarcCarcEnabled && isTaggingEnabled
  const hasTaggingBeenReviewed =
    document.reviewed_step === 'carc_rarc' ||
    document.reviewed_step === 'patient' ||
    document.review_status === 'reviewed' ||
    document.review_status === 'auto_reviewed'
  const isHideLlmClassificationEnabled = useIsFeatureEnabled('2026-08-hide-llm-classification')
  const isPriorAuthClassification = PRIOR_AUTH_PHASES.includes(document.category_phase ?? '')
  const hasClassificationBeenReviewed = getIsClassificationFinished(document, subcategories)
  const hideLlmClassificationUntilReviewed =
    isHideLlmClassificationEnabled && isPriorAuthClassification && !hasClassificationBeenReviewed
  const { data: paMatchesData } = useQuery({
    ...apiClient.getDocumentPatientPaMatchesDocumentsKeyPatientPaMatchesGetOptions({
      path: { key: documentKey },
    }),
    enabled: isPriorAuthClassification,
  })
  const events = useMemo(
    () =>
      processEvents(serverEvents, {
        subcategories,
        payors,
        isTaggingEnabled,
        hideLlmRarcCarcUntilReviewed,
        isTaggingFinished: hasTaggingBeenReviewed,
        hideLlmClassificationUntilReviewed,
        isClassificationFinished: hasClassificationBeenReviewed,
        paPatientData: paMatchesData?.patient_data ?? undefined,
      }),
    [
      serverEvents,
      subcategories,
      payors,
      isTaggingEnabled,
      hideLlmRarcCarcUntilReviewed,
      hasTaggingBeenReviewed,
      hideLlmClassificationUntilReviewed,
      hasClassificationBeenReviewed,
      paMatchesData?.patient_data,
    ],
  )

  const reviewers = useMemo(() => {
    return uniqBy(
      events.map((event) => event.user).filter((user) => !!user),
      'sub',
    )
  }, [events])

  const selectedReviewerNames = useMemo(() => {
    return reviewerEmails.map((email) => {
      const reviewer = reviewers.find((reviewer) => reviewer.sub === email)
      if (!reviewer) {
        return email
      }
      return getDisplayName(reviewer)
    })
  }, [reviewerEmails, reviewers])

  // Filter events by current step and search value
  const filteredEvents = useMemo(() => {
    return events
      .filter((event) => {
        const matchesStep =
          !step || step === event.step || (event.visibleInSteps?.includes(step) ?? false)
        const matchesReviewer =
          !reviewerEmails.length ||
          (reviewerEmails.includes('opti') && event.userRole === 'opti') ||
          (event.user && reviewerEmails.includes(event.user.sub))
        const lowerQuery = searchQuery?.toLowerCase()
        const matchesSearch =
          !lowerQuery ||
          getSearchableText(event).toLowerCase().includes(lowerQuery) ||
          (event.userRole === 'person' &&
            [event.user?.first_name, event.user?.last_name]
              .join(' ')
              .toLowerCase()
              .includes(lowerQuery)) ||
          (event.userRole === 'opti' && 'opti'.includes(lowerQuery))
        return matchesStep && matchesSearch && matchesReviewer
      })
      .sort((a, b) =>
        sorting === 'newest' ? b.timestamp - a.timestamp : a.timestamp - b.timestamp,
      )
  }, [events, step, reviewerEmails, searchQuery, sorting])

  // Group events by date
  const groupedEvents = useMemo(() => {
    const groups: Record<string, typeof filteredEvents> = {}
    filteredEvents.forEach((event) => {
      const date = new Date(event.timestamp)
      let label: string
      if (isToday(date)) {
        label = `Today · ${format(date, 'MMMM d, yyyy')}`
      } else if (isYesterday(date)) {
        label = `Yesterday · ${format(date, 'MMMM d, yyyy')}`
      } else {
        label = format(date, 'MMMM d, yyyy')
      }
      if (!groups[label]) {
        groups[label] = []
      }
      groups[label].push(event)
    })
    return groups
  }, [filteredEvents])

  return (
    <div
      className={cn('border-divider flex h-full w-[480px] shrink-0 flex-col border-l', className)}
    >
      <div className="border-divider shrink-0 border-b">
        <div className="flex h-[72px] items-center justify-between px-6">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Text appearance="H6">Audit Trail</Text>
              <InfoTip>
                <div className="flex w-[220px] flex-col gap-3">
                  <Text appearance="SUBTITLE2">Audit Trail Legend</Text>
                  <div className="grid grid-cols-[64px_1fr] items-center gap-y-0.5">
                    <Icon
                      className="text-action-active text-[18px]"
                      children="wb_incandescent"
                    />
                    <Text
                      className="text-text-secondary"
                      appearance="CAPTION"
                    >
                      Suggested by opti
                    </Text>
                    <Icon
                      className="text-action-active text-[18px]"
                      children="add"
                    />
                    <Text
                      className="text-text-secondary"
                      appearance="CAPTION"
                    >
                      Field or tag added
                    </Text>
                    <Icon
                      className="text-action-active text-[18px]"
                      children="delete"
                    />
                    <Text
                      className="text-text-secondary"
                      appearance="CAPTION"
                    >
                      Field or tag removed
                    </Text>
                    <span className="text-action-active ml-[3px]">--</span>
                    <Text
                      className="text-text-secondary"
                      appearance="CAPTION"
                    >
                      Empty field
                    </Text>
                    <span className="text-text-tertiary ml-[3px]">
                      <span className="line-through">data</span> →
                    </span>
                    <Text
                      className="text-text-secondary"
                      appearance="CAPTION"
                    >
                      Existing data updated
                    </Text>
                  </div>
                </div>
              </InfoTip>
            </div>
            <Text
              className="text-text-secondary uppercase"
              appearance="BODY2"
            >
              {assetId}
            </Text>
          </div>
          <IconButton
            container="small"
            materialIcon="close"
            onClick={closeAuditTrail}
          />
        </div>

        <div className="flex h-[48px] items-center justify-between gap-2 px-3 whitespace-nowrap">
          <div className="flex items-center gap-2">
            <StepFilter
              selectedStep={step}
              onSelectStep={handleSetStep}
              steps={stepFilterOptions}
            />
            <ReviewerFilter
              reviewers={reviewers}
              reviewerEmails={reviewerEmails}
              setReviewerEmails={setReviewerEmails}
            />
          </div>
          <div className="flex items-center gap-2">
            <IconButton
              container="medium"
              onClick={() => {
                setSorting((prev) => (prev === 'newest' ? 'oldest' : 'newest'))
              }}
            >
              <LocalIcon
                className={cn('fill-content-medium-emphasis', {
                  'rotate-180': sorting === 'oldest',
                })}
                name="sort"
              />
            </IconButton>
            <SearchFilter
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
            />
          </div>
        </div>
      </div>

      <div className="border-divider flex shrink-0 items-center justify-between gap-4 border-b px-4 py-1.5">
        <div className="flex flex-wrap items-center gap-2">
          {step !== null && (
            <Chip
              outlined
              selected
              onClose={() => handleSetStep(null)}
            >
              {getStepLabel(step)}
            </Chip>
          )}
          {reviewerEmails.length > 0 && (
            <Chip
              outlined
              selected
              onClose={() => setReviewerEmails([])}
            >
              {selectedReviewerNames.join(', ')}
            </Chip>
          )}
          {searchQuery && (
            <Chip
              outlined
              selected
              onClose={() => setSearchQuery('')}
            >
              {searchQuery}
            </Chip>
          )}
        </div>
        <Button
          disabled={!isDirty}
          size="medium"
          onClick={clearFilters}
        >
          Clear Filters
        </Button>
      </div>

      <ScrollArea className="flex-1">
        {hideLlmClassificationUntilReviewed && (step === null || step === 'classification') && (
          <div className="px-6 pt-4">
            <div className="flex gap-3">
              <div className="relative flex w-6 shrink-0 justify-center">
                <div className="bg-content-low-emphasis absolute top-0 h-full w-0.5" />
                <div className="bg-content-medium-emphasis-no-alpha relative top-1 h-2.5 w-2.5 rounded-full" />
              </div>
              <div className="bg-surface-low border-divider flex flex-1 items-start gap-2 rounded-lg border p-3">
                <Icon className="text-content-medium-emphasis text-[18px]">lock</Icon>
                <div className="flex flex-col gap-0.5">
                  <Text appearance="SUBTITLE2">Classification: Awaiting Validation</Text>
                  <Text
                    className="text-text-secondary"
                    appearance="BODY2"
                  >
                    The suggested classification is hidden until the reviewer has answered
                  </Text>
                </div>
              </div>
            </div>
          </div>
        )}

        {Object.entries(groupedEvents).map(([dateLabel, events], groupIndex) => (
          <CollapsibleRoot key={dateLabel}>
            <CollapsibleTrigger
              className="h-12 px-6"
              labelClassName="text-text-secondary"
              label={dateLabel}
            />
            <CollapsibleContent className="px-6">
              {events.map((event, eventIndex) => {
                const isFirstInFirstGroup = eventIndex === 0 && groupIndex === 0
                const isLastInLastGroup =
                  eventIndex === events.length - 1 &&
                  groupIndex === Object.entries(groupedEvents).length - 1
                const EventComponent = getEventComponent(event)

                return (
                  <EventShell
                    key={`${event.timestamp}-${eventIndex}`}
                    event={event}
                    searchQuery={searchQuery}
                    isFirstInFirstGroup={isFirstInFirstGroup}
                    isLastInLastGroup={isLastInLastGroup}
                  >
                    <EventComponent
                      event={event}
                      searchQuery={searchQuery}
                    />
                  </EventShell>
                )
              })}
            </CollapsibleContent>
          </CollapsibleRoot>
        ))}

        {/* Empty state */}
        {Object.entries(groupedEvents).length === 0 && !hideLlmClassificationUntilReviewed && (
          <div className="flex h-full flex-col items-center">
            <Imagery
              className="text-content-low-emphasis mt-[100px]"
              name="searchResultsAlt"
            />
            <Text
              className="text-text-secondary mt-6"
              appearance="H6"
            >
              {hideLlmRarcCarcUntilReviewed && !hasTaggingBeenReviewed
                ? 'No audit activity yet'
                : 'Hmm, nothing turned up...'}
            </Text>
            <Text
              className="text-text-tertiary mt-4"
              appearance="BODY2"
            >
              {hideLlmRarcCarcUntilReviewed && !hasTaggingBeenReviewed
                ? 'Validate this step to see the audit trail.'
                : 'No matching records found.'}
            </Text>
          </div>
        )}
      </ScrollArea>
    </div>
  )
}
